import { Menu } from "@mui/material";
import type { GridPinnedColumnFields, GridRowGroupingModel, GridSortModel } from "@mui/x-data-grid-premium";
import { isGroupingColumn } from "@mui/x-data-grid-premium";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import type { ColumnHorizontalAlignment } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { CalculationType } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { CustomColumnField } from "../../../../../../../../api/types/svTypes.ts";
import { useBiCommonDataContext } from "../../../../contexts/BiCommonDataContext.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch";
import { selectPreviewMode } from "../../../../store/selectors/currentReportSelectors";
import {
  selectSorts,
  selectTabularFields,
  selectTabularGrouping,
  selectTabularSettingsState,
} from "../../../../store/selectors/fieldsStateSelectors";
import { selectEditDisabled } from "../../../../store/selectors/uiSettingsSelectors.ts";
import { fieldsStateActions } from "../../../../store/thunks/fieldStatesThunks";
import { tabularFieldsStateThunks } from "../../../../store/thunks/tabularFieldsStateThunks.ts";
import { createTabularAction } from "../../../../store/utils/storeUtils.ts";
import {
  buildCustomColumnConfigChanges,
  isCustomColumn,
  newCustomColumnField,
} from "../../../../utilities/fieldsHelper.ts";
import type { TabularGridState } from "../../../viewer/tabular/hooks/gridState.types.ts";
import PreviewComponent from "../../../viewer/tabular/PreviewComponent.tsx";
import { getGroupings } from "../../../viewer/tabular/utils/getGrouping.ts";
import type { CustomColumnData } from "../../common/fields/custom-field/customColumn.types.ts";
import CustomColumnConditionValueSetup from "../../common/fields/custom-field/CustomColumnConditionValueSetup.tsx";
import CustomFormulaDialog from "../../common/fields/custom-field/CustomFormulaDialog.tsx";
import NewCustomColumnCalculationSetup from "../../common/fields/custom-field/NewCustomColumnCalculationSetup.tsx";
import { findExistingCustomFormulaField } from "../../common/fields/custom-field/utilities/findExistingCustomFormulaField.ts";
import useCommonReportHandlers from "../../common/hooks/useCommonReportHandlers";
import { createTabularField } from "../../common/utilities/createFields.ts";

interface Props {
  gridState: TabularGridState;
}
const EditPreviewComponent = ({ gridState }: Props) => {
  const dispatch = useAppDispatch();
  const { onRefreshGrid } = useBiCommonDataContext();
  const editDisabled = useSelector(selectEditDisabled);
  const fieldsAreaValues = useSelector(selectTabularFields);
  const sortingAreaValues = useSelector(selectSorts);
  const groupAreaValues = useSelector(selectTabularGrouping);
  const settingsAreaValues = useSelector(selectTabularSettingsState);
  const previewMode = useSelector(selectPreviewMode);
  const handlers = useCommonReportHandlers(fieldsAreaValues);

  const [existingCustomFormulaField, setExistingCustomFormulaField] = useState<CustomColumnField | undefined>(
    undefined
  );

  const onColumnsSortingChange = useCallback(
    (newSorting: GridSortModel) => {
      dispatch(tabularFieldsStateThunks.updateSorting(newSorting));
    },
    [dispatch]
  );

  const handleGroupingModelChange = useCallback(
    (model: GridRowGroupingModel) => {
      const { groupIdsToAdd, groupsToDelete } = getGroupings(model, groupAreaValues);

      groupIdsToAdd.forEach((groupId) => {
        dispatch(fieldsStateActions.addGroup({ id: groupId }));
      });

      groupsToDelete.forEach((group) => {
        dispatch(fieldsStateActions.removeGroup(group));
      });
    },
    [dispatch, groupAreaValues]
  );

  const updateFieldsOrder = useCallback(
    (names: string[]) => {
      dispatch(fieldsStateActions.updateFieldsOrder(names));
    },
    [dispatch]
  );

  const onFreezedColumnsChange = useCallback(
    (freezedColumns: GridPinnedColumnFields) => {
      dispatch(fieldsStateActions.setFreezedColumns(freezedColumns));
    },
    [dispatch]
  );

  const handleColumnWidthChanged = useCallback(
    (field: string, width: number | undefined, applyToAll?: boolean) => {
      if (editDisabled) {
        return;
      }
      if (applyToAll) {
        dispatch(createTabularAction("updateAllColumnWidths", { width }));
        return;
      }
      if (isGroupingColumn(field)) {
        dispatch(createTabularAction("updateGroupingColumnWidth", { field, width }));
        return;
      }
      const reportField = fieldsAreaValues.find((item) => item.config.guid === field);
      const autosize = width === undefined;
      if (
        reportField &&
        (reportField.config.customizations?.columnWidth !== width ||
          reportField.config.customizations?.autosize !== autosize)
      ) {
        dispatch(
          createTabularAction("updateFieldConfig", {
            field: reportField,
            changes: { customizations: { ...reportField.config.customizations, autosize, columnWidth: width } },
          })
        );
      }
    },
    [dispatch, editDisabled, fieldsAreaValues]
  );

  const handleColumnAlignmentChanged = useCallback(
    (field: string, alignment: ColumnHorizontalAlignment | undefined) => {
      if (editDisabled) {
        return;
      }
      if (isGroupingColumn(field)) {
        dispatch(createTabularAction("updateGroupingColumnAlignment", { field, alignment }));
        return;
      }
      const reportField = fieldsAreaValues.find((item) => item.config.guid === field);
      if (reportField && (reportField.config.customizations?.horizontalAlignment ?? undefined) !== alignment) {
        dispatch(
          createTabularAction("updateFieldConfig", {
            field: reportField,
            changes: { customizations: { ...reportField.config.customizations, horizontalAlignment: alignment } },
          })
        );
      }
    },
    [dispatch, editDisabled, fieldsAreaValues]
  );

  const [customCalculationState, setCustomCalculationState] = useState<{
    headerCellRef: HTMLElement | null;
    userSelectedCalculationType: CalculationType;
    fieldGuid: string;
  } | null>(null);

  const onPredefinedCustomFieldType = useCallback(
    (calculationType: CalculationType, fieldGuid: string, anchorEl: HTMLElement | null) => {
      setCustomCalculationState({
        headerCellRef: anchorEl,
        userSelectedCalculationType: calculationType,
        fieldGuid: fieldGuid,
      });
      setExistingCustomFormulaField(findExistingCustomFormulaField(fieldsAreaValues, fieldGuid));
    },
    [fieldsAreaValues]
  );

  const onApplySetupCustomCalculationMenu = (data: CustomColumnData) => {
    const currentField = fieldsAreaValues.find((field) => field.config.guid === customCalculationState?.fieldGuid);
    if (isCustomColumn(currentField)) {
      dispatch(
        fieldsStateActions.updateFieldConfig({
          field: currentField,
          changes: buildCustomColumnConfigChanges(data),
        })
      );
    } else {
      const field = newCustomColumnField(data);
      dispatch(fieldsStateActions.addField({ field: createTabularField(field.meta), index: fieldsAreaValues.length }));
    }
    setCustomCalculationState(null);
  };

  const onCloseSetupCustomCalculationMenu = () => {
    setCustomCalculationState(null);
  };

  return (
    <>
      <PreviewComponent
        onRefresh={onRefreshGrid}
        gridState={gridState}
        fields={fieldsAreaValues}
        groups={groupAreaValues}
        onColumnsSortingChanged={onColumnsSortingChange}
        onColumnsOrderChanged={updateFieldsOrder}
        onFreezedColumnsChange={onFreezedColumnsChange}
        onColumnWidthChanged={handleColumnWidthChanged}
        onColumnAlignmentChanged={handleColumnAlignmentChanged}
        onGroupingChanged={handleGroupingModelChange}
        onDrillDown={handlers.onDrillDown}
        previewMode={previewMode}
        settings={settingsAreaValues}
        sorts={sortingAreaValues}
        disableColumnManagement={editDisabled}
        onPredefinedCustomFieldType={onPredefinedCustomFieldType}
      />
      {customCalculationState && (
        <>
          {customCalculationState.userSelectedCalculationType === CalculationType.ConditionValue && (
            <Menu
              open={true}
              anchorEl={customCalculationState.headerCellRef}
              onClose={onCloseSetupCustomCalculationMenu}
              slotProps={{ paper: { sx: { width: 400 } } }}
              disableRestoreFocus
            >
              <CustomColumnConditionValueSetup
                fieldGuid={customCalculationState.fieldGuid}
                onClose={onCloseSetupCustomCalculationMenu}
                onApply={onApplySetupCustomCalculationMenu}
                readonly={false}
              />
            </Menu>
          )}
          {customCalculationState.userSelectedCalculationType === CalculationType.PercentageOfTotal && (
            <Menu
              open={true}
              anchorEl={customCalculationState.headerCellRef}
              onClose={onCloseSetupCustomCalculationMenu}
              slotProps={{ paper: { sx: { width: 400 } } }}
              disableRestoreFocus //Prevents the autofocus of the TextField from not working
            >
              <NewCustomColumnCalculationSetup
                fieldGuid={customCalculationState.fieldGuid}
                calculationType={customCalculationState.userSelectedCalculationType}
                onClose={onCloseSetupCustomCalculationMenu}
                onApply={onApplySetupCustomCalculationMenu}
                readonly={false}
              />
            </Menu>
          )}
          {customCalculationState.userSelectedCalculationType === CalculationType.CustomFormula && (
            <CustomFormulaDialog
              open
              onClose={onCloseSetupCustomCalculationMenu}
              onApply={onApplySetupCustomCalculationMenu}
              editedField={existingCustomFormulaField}
              sourceFieldGuid={customCalculationState.fieldGuid}
            />
          )}
        </>
      )}
    </>
  );
};

export default EditPreviewComponent;
